import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  Categories,
  IMomentMailErrorData,
  IMomentTemplate,
  MomentTemplateStatus
} from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import styled from '@emotion/styled';
import {
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Link,
  Tooltip,
  Typography
} from '@material-ui/core';
import { sortBy } from 'lodash';
import { Copy, Edit, Eye, PlusCircle, Search } from 'lucide-react';
import moment from 'moment-timezone';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { ColoredChip } from '../../../../components/ColoredChip';
import { DadoAIBanner } from '../../../../components/DadoAIBanner';
import { DialogTitleWithColor } from '../../../../components/DialogTitleWithColor';
import { DialogTitleWithTypography } from '../../../../components/DialogTitleWithTypography';
import {
  ItemSorters,
  ROW_HEIGHTS,
  RowsRenderer,
  useSortQueryParam
} from '../../../../components/GroupableList';
import { LinkExternal } from '../../../../components/LinkExternal';
import { InlineLoader, Loader } from '../../../../components/Loader';
import {
  SomethingWentWrong,
  SomethingWentWrongWithReport
} from '../../../../components/SomethingWentWrong';
import { IColumn } from '../../../../components/Table/VirtualizedSortable';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useErrorLogger } from '../../../../hooks/useErrorLogger';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../i18next';
import { Centered } from '../../../../layout/Centered';
import { FlexContainer } from '../../../../layout/Flex';
import { PageBody } from '../../../../layout/PageBody';
import {
  PageToolbar,
  PageToolbarActions,
  PageToolbarTitle
} from '../../../../layout/PageToolbar';
import {
  useBooleanQueryParam,
  useRoutes,
  useTypedStringQueryParam
} from '../../../../routes';
import { useAdminUserById } from '../../../../services/adminUsers';
import { useCurrentUser } from '../../../../services/currentUser';
import { useFeatureEnabled } from '../../../../services/features';
import { useMappedLoadingValue } from '../../../../services/firebase';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { toFullName } from '../../../../services/user';
import { useTokenDefinitionsForMoment } from '../../../Experiences/components/Builders/TaskBuilder/components/BlockSettings/common';
import { MomentTemplateStatusChip } from '../../components/StatusChip';
import {
  createNewMomentTemplate,
  downloadMomentReport,
  duplicateMomentTemplate,
  resendMoment,
  useMomentMailErrorData,
  useMomentMailEventReport,
  useMomentTemplatesByOrgId
} from '../../services';
import {
  CategoriesLabels,
  duplicateMomentTemplateTemplate,
  useMomentTemplateTemplates
} from '../../services/templateTemplates';
import { MomentContentPreviewDialog } from '../Details/components/Content';

const HIDE_FOR_V1 = true;

const SignUpSuccessDialog = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitleWithColor color="green">
        {t('Your Moments account is ready to use')}
      </DialogTitleWithColor>
      <DialogContent style={{ paddingBottom: 0 }}>
        <p>
          {t(`If you'd like some tips on what to do first, check out our`)}{' '}
          <LinkExternal
            color="primary"
            href={
              'https://help.dadohr.com/article/108-getting-started-with-dado-moments'
            }
          >
            {t('Getting Started guide')}
          </LinkExternal>
          .
        </p>
        <Typography>
          {t(
            `Our help center is always accessible via the support link in the top right, or you can email our support team on`
          )}{' '}
          <LinkExternal color="primary" href="mailto:help@dadohr.com">
            help@dadohr.com
          </LinkExternal>
          .
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type MomentFilters = 'ALL' | MomentTemplateStatus;

const MomentListSection = styled('div')((p) => ({
  marginTop: p.theme.spacing(3)
}));

export const MomentCommonCardStyle = styled('div')((p) => ({
  display: 'grid',
  border: `1px solid ${p.theme.custom.border.color}`,
  borderRadius: p.theme.custom.border.radius(),
  padding: p.theme.spacing(3),
  transition: 'all 0.2s ease-in',
  height: '100%',
  gap: p.theme.spacing(),
  justifyContent: 'space-between',
  alignItems: 'center',

  ':hover': {
    boxShadow: `0 0 0 4px ${p.theme.custom.colors.gray.w200}!important`
  }
}));

const TemplateCardContainer = styled(MomentCommonCardStyle)((p) => ({
  gridTemplateColumns: '1fr 1fr 3fr 1fr'
}));

const TemplateTemplateCardContainer = styled(MomentCommonCardStyle)((p) => ({
  cursor: 'pointer',
  gridTemplateColumns: '1fr min-content'
}));

const FilterItem = styled('div')<{ selected: boolean }>((p) => ({
  fontSize: p.theme.custom.fontSize.l,
  width: 'fit-content',
  borderBottom: '3px solid transparent',
  borderColor: p.selected ? `${p.theme.custom.colors.green.w400}` : 'none',
  fontWeight: p.selected ? p.theme.typography.fontWeightBold : undefined
}));

const ClickableBadgeContainer = styled('div')((p) => ({
  cursor: 'pointer',
  padding: p.theme.spacing(1),
  paddingLeft: 0
}));

const StatusFilters = ({
  filter,
  setFilter,
  canOnlySeeTemplates
}: {
  filter: MomentFilters;
  setFilter: (nextFilter: MomentFilters) => void;
  canOnlySeeTemplates: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <FlexContainer spacing={2}>
      {!canOnlySeeTemplates && (
        <>
          <FilterButton
            title={t('All')}
            active={filter === 'ALL'}
            onClick={() => setFilter('ALL')}
          />
          <FilterButton
            title={t('Draft')}
            active={filter === MomentTemplateStatus.DRAFT}
            onClick={() => setFilter(MomentTemplateStatus.DRAFT)}
          />
          <FilterButton
            title={t('Scheduled')}
            active={filter === MomentTemplateStatus.SCHEDULED}
            onClick={() => setFilter(MomentTemplateStatus.SCHEDULED)}
          />
          <FilterButton
            title={t('Sent')}
            active={filter === MomentTemplateStatus.COMPLETE}
            onClick={() => setFilter(MomentTemplateStatus.COMPLETE)}
          />
        </>
      )}
      <FilterButton
        title={t('Templates')}
        active={filter === MomentTemplateStatus.TEMPLATE}
        onClick={() => setFilter(MomentTemplateStatus.TEMPLATE)}
      />
    </FlexContainer>
  );
};

const Actions = ({ d }: { d: IMomentMailErrorData }) => {
  const { t } = useUnsafeTranslation();
  const [resendStatus, setResendStatus] = useState<
    'COMPLETED' | 'FAILED' | null
  >(null);
  const [resendFailReason, setResendFailReason] = useState<string | null>(null);

  if (resendStatus === 'COMPLETED') {
    return <>{t('Email sent')}</>;
  }
  if (resendStatus === 'FAILED') {
    return (
      <>
        {t('Unable to resend email - {{reason}}', { reason: resendFailReason })}
      </>
    );
  }
  return (
    <ButtonWithPromise
      variant="outlined"
      color="primary"
      size="small"
      pending={t('Resending')}
      onClick={async () => {
        try {
          await resendMoment(d.orgId, d.tplId, d.instanceId);
          setResendStatus('COMPLETED');
        } catch (error: any) {
          setResendStatus('FAILED');
          setResendFailReason(error.message);
        }
      }}
    >
      {t('Retry')}
    </ButtonWithPromise>
  );
};

type ColumnKey = 'action' | 'email' | 'issue' | 'name';

const MAIL_ERROR_TABLE_CELLS: IColumn<IMomentMailErrorData, ColumnKey>[] = [
  {
    key: 'name',
    head: () => 'Name',
    cell: (d) => d.user.fullName,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'email',
    head: () => 'Email',
    cell: (d) => d.user.email,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'issue',
    head: () => 'Issue',
    cell: (d) =>
      d.error === 'BOUNCED'
        ? 'Bounced'
        : d.error === 'NO_EMAIL'
        ? 'No email address'
        : 'Unknown error',
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 200,
    flexGrow: 1
  },
  {
    key: 'action',
    head: () => '',
    cell: (d) => <Actions d={d} />,
    align: 'right',
    sortable: false,
    width: 200
  }
];

const MAIL_ERROR_TABLE_SORTERS: ItemSorters<IMomentMailErrorData> = {
  name: {
    key: 'name',
    items: { sort: (d) => d.user.fullName, dir: 'asc' }
  },
  email: {
    key: 'email',
    items: { sort: (d) => d.user.email, dir: 'asc' }
  },
  issue: {
    key: 'issue',
    items: { sort: (d) => d.error, dir: 'asc' }
  }
};

const MailErrorTable = ({ orgId, tplId }: { orgId: string; tplId: string }) => {
  const [data, loading, error] = useMomentMailErrorData(orgId, tplId);
  const [[sorter, direction], setSort] = useSortQueryParam(
    'sort',
    MAIL_ERROR_TABLE_SORTERS
  );

  if (loading) {
    return <Loader />;
  }
  if (error || !data) {
    return (
      <SomethingWentWrongWithReport
        error={error}
        context={{ page: 'moments_overview', section: 'mail_errors' }}
      />
    );
  }
  return (
    <RowsRenderer
      variant="contained"
      columns={MAIL_ERROR_TABLE_CELLS}
      rows={data}
      rowToKey={(row) => row.instanceId}
      renderHead={true}
      headProps={{ sticky: true, offset: 0 }}
      onHeadClick={(c, dir) =>
        setSort([MAIL_ERROR_TABLE_SORTERS[c.key] || null, dir])
      }
      rowHeight={ROW_HEIGHTS.compact}
      sorter={sorter ?? MAIL_ERROR_TABLE_SORTERS.email}
      sortDirection={direction ?? 'asc'}
      otherProps={undefined}
    />
  );
};

const MomentStatsCells = ({
  tplId,
  tplName,
  orgId
}: {
  tplId: string;
  tplName: string;
  orgId: string;
}) => {
  const { t } = useUnsafeTranslation();
  const [d, loading, error] = useMomentMailEventReport(orgId, tplId);
  const { trackEvent } = useProductAnalytics();
  const { dialogOpen, openDialog, closeDialog } = useDialogState();
  useErrorLogger('MomentStats', error);

  const openDialogAndTrack = () => {
    trackEvent({
      name: 'Moments Bounces List Clicked',
      Explanation: 'Admin user views the modal showing bounces for a Moment',
      eventType: PAEventType.OTHER,
      'Moment ID': tplId,
      'Moment Name': tplName
    });
    openDialog();
  };

  return (
    <>
      <FlexContainer direction="column" alignItems="flex-start">
        <Typography variant="h6">{t('Sent')}</Typography>
        <Typography>
          {loading ? <InlineLoader /> : d ? d.totals.d : null}
        </Typography>
      </FlexContainer>
      <FlexContainer direction="column" alignItems="flex-start">
        <Typography variant="h6">{t('Opens')}</Typography>
        <Typography>
          {loading ? <InlineLoader /> : d ? d.totals.o : null}
        </Typography>
      </FlexContainer>
      <FlexContainer direction="column" alignItems="flex-start">
        <Typography variant="h6">{t('Clicks')}</Typography>
        <Typography>
          {loading ? <InlineLoader /> : d ? d.totals.c : null}
        </Typography>
      </FlexContainer>
      <FlexContainer direction="column" alignItems="flex-start">
        <Typography variant="h6">{t('Bounces')}</Typography>
        {loading ? (
          <InlineLoader />
        ) : (d?.totals.b ?? 0) > 0 ? (
          <Button
            type="button"
            color="primary"
            variant="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault(); // Interrupt moment card route link
              openDialogAndTrack();
            }}
          >
            <Typography>{d!.totals.b}</Typography>
            <div onClick={(e) => e.stopPropagation()}>
              <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                fullWidth
                maxWidth="md"
              >
                <DialogTitleWithTypography>
                  {t('Bounces')}
                </DialogTitleWithTypography>
                <DialogContent>
                  <MailErrorTable orgId={orgId} tplId={tplId} />
                </DialogContent>
                <DialogActions>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={(e) => {
                      closeDialog();
                    }}
                  >
                    {t('Close')}
                  </Button>
                </DialogActions>
              </Dialog>
            </div>
          </Button>
        ) : (
          <Typography>0</Typography>
        )}
      </FlexContainer>
    </>
  );
};

const FilterButton = ({
  title,
  active,
  onClick
}: {
  title: string;
  active: boolean;
  onClick: () => void;
}) => {
  return (
    <ButtonBase onClick={onClick}>
      <FilterItem selected={active}>{title}</FilterItem>
    </ButtonBase>
  );
};

export const AdminUserName = ({
  orgId,
  userId,
  prefix,
  suffix
}: {
  orgId: string;
  userId: string;
  prefix?: string;
  suffix?: string;
}) => {
  const [admin] = useAdminUserById(orgId, userId);
  return admin ? (
    <Typography>
      {prefix && `${prefix} `}
      {toFullName(admin.data)}
      {suffix && ` ${suffix}`}
    </Typography>
  ) : null;
};

export const MomentTemplateTitleColumn = ({
  d
}: {
  d: Doc<IMomentTemplate>;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <FlexContainer direction="column" alignItems="flex-start" spacing={1}>
      <Typography color="primary">
        {d.data.title || t('Unnamed Moment')}
      </Typography>
      {d.data.orgId && (
        <AdminUserName orgId={d.data.orgId} userId={d.data.createdBy} />
      )}
    </FlexContainer>
  );
};

export const MomentTemplateStatusAndDateColumn = ({
  d,
  org
}: {
  d: Doc<IMomentTemplate>;
  org: IOrganization;
}) => {
  return (
    <FlexContainer direction="column" alignItems="flex-start" spacing={1}>
      <MomentTemplateStatusChip status={d.data.status} />
      {d.data.status !== MomentTemplateStatus.TEMPLATE && (
        <Typography variant="body1">
          {moment(d.data.sendDate.at.toMillis())
            .tz(d.data.tz)
            .format(`${org.dateFormats.MMDDYYYY}`)}
        </Typography>
      )}
    </FlexContainer>
  );
};

const TemplateCard = ({
  momentDoc,
  linkTo,
  onDuplicate,
  onReportDownload
}: {
  onDuplicate?: (
    m: string,
    duplicate: 'True' | 'False',
    duplicatedMoment: Doc<IMomentTemplate>
  ) => void;
  onReportDownload?: (momentName: string, momentId: string) => void;
  momentDoc: Doc<IMomentTemplate>;
  linkTo: string;
}) => {
  const d = momentDoc.data;
  const { t } = useUnsafeTranslation();
  const currentUser = useCurrentUser();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();

  const view = t('View');
  const duplicate = t('Duplicate');
  const edit = t('Edit');

  return (
    <RouterLink to={linkTo}>
      <TemplateCardContainer>
        <MomentTemplateTitleColumn d={momentDoc} />
        <MomentTemplateStatusAndDateColumn
          d={momentDoc}
          org={currentUser.org}
        />
        <FlexContainer justifyContent="flex-start" spacing={8}>
          {d.status === MomentTemplateStatus.COMPLETE && (
            <MomentStatsCells
              tplId={momentDoc.id}
              orgId={d.orgId}
              tplName={momentDoc.data.title}
            />
          )}
        </FlexContainer>
        <FlexContainer
          justifyContent="flex-end"
          alignItems="center"
          spacing={4}
        >
          <FlexContainer
            justifyContent="flex-end"
            alignItems="center"
            spacing={2}
          >
            {d.status === MomentTemplateStatus.COMPLETE && (
              // If this is too slow we can use the BackgroundJobs
              // system to run this asynchronously
              <ButtonWithPromise
                variant="text"
                color="primary"
                onClick={(ev) =>
                  downloadMomentReport(momentDoc, { format: 'csv' }).then(
                    () => {
                      onReportDownload?.(momentDoc.data.title, momentDoc.id);
                    }
                  )
                }
                pending={t('Downloading...')}
                noPropagation
              >
                {t('Download report')}
              </ButtonWithPromise>
            )}
            {d.status === MomentTemplateStatus.DRAFT ? (
              <Tooltip title={edit}>
                <IconButton>
                  <Edit size={20} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={view}>
                <IconButton>
                  <Eye size={20} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={duplicate}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (d.status === MomentTemplateStatus.TEMPLATE) {
                    duplicateMomentTemplateTemplate(momentDoc, currentUser)
                      .then((duplicatedMoment) => {
                        onDuplicate?.(duplicatedMoment.id, 'True', momentDoc);
                        enqueueSnackbarSuccess(
                          t(`Created new moment based on '${d.title}'`)
                        );
                      })
                      .catch((e) => {
                        console.warn(e);
                        enqueueSnackbarError(
                          t('Something went wrong. Please try again later')
                        );
                      });
                    return;
                  }

                  duplicateMomentTemplate(momentDoc, currentUser.id)
                    .then((duplicatedMoment) => {
                      onDuplicate?.(duplicatedMoment.id, 'True', momentDoc);
                      enqueueSnackbarSuccess(
                        t(`Created new moment based on '${d.title}'`)
                      );
                    })
                    .catch((e) => {
                      console.warn(e);
                      enqueueSnackbarError(
                        t('Something went wrong. Please try again later')
                      );
                    });
                }}
              >
                <Copy size={20} />
              </IconButton>
            </Tooltip>
          </FlexContainer>
        </FlexContainer>
      </TemplateCardContainer>
    </RouterLink>
  );
};

const MomentTemplateList = ({
  tpls,
  loading,
  error,
  onDuplicate,
  onReportDownload
}: {
  tpls: void | Doc<IMomentTemplate>[];
  loading: boolean;
  error: any;

  onDuplicate: (
    m: string,
    duplicate: 'True' | 'False',
    duplicatedMoment: Doc<IMomentTemplate>
  ) => void;
  onReportDownload: (momentName: string, momentId: string) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { ROUTES } = useRoutes();
  if (loading) {
    return <Loader height={300} />;
  }
  if (error || !tpls) {
    return (
      <SomethingWentWrongWithReport
        height={300}
        error={error}
        context={{ page: 'moments_overview', section: 'moment_list' }}
      />
    );
  }

  return (
    <>
      {tpls.length ? (
        <FlexContainer
          justifyContent="flex-start"
          alignItems="stretch"
          direction="column"
          spacing={3}
        >
          {tpls.map((m) => (
            <TemplateCard
              key={m.id}
              momentDoc={m}
              onDuplicate={onDuplicate}
              onReportDownload={onReportDownload}
              linkTo={ROUTES.moments.details.url({
                momentTemplateId: m.id
              })}
            />
          ))}
        </FlexContainer>
      ) : (
        <Centered height={300}>{t('No moments here')}</Centered>
      )}
    </>
  );
};

const TemplateTemplateCard = ({ d }: { d: Doc<IMomentTemplate> }) => {
  const { t } = useUnsafeTranslation();
  const currentUser = useCurrentUser();
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
  const previewDialog = useDialogState();
  const tokens = useTokenDefinitionsForMoment(
    currentUser.org.id,
    currentUser.org.customUserFields
  );
  const { goTo, ROUTES } = useRoutes();
  const momentsEnabled = useFeatureEnabled('MOMENTS_V1');
  return (
    <>
      <TemplateTemplateCardContainer
        role="button"
        onClick={() => {
          previewDialog.openDialog();
        }}
      >
        <FlexContainer direction="column" alignItems="flex-start" spacing={1}>
          <Typography color="primary">
            {d.data.title || t('Unnamed Moment')}
          </Typography>
          {!!d.data.categories.length && (
            <FlexContainer>
              {d.data.categories.map((c) => (
                <ColoredChip key={c.type} color="gray" size="small">
                  {CategoriesLabels[c.type](t)}
                </ColoredChip>
              ))}
            </FlexContainer>
          )}
        </FlexContainer>

        <FlexContainer
          justifyContent="flex-end"
          alignItems="center"
          spacing={2}
        >
          <Tooltip title={`${t('View')}`}>
            <IconButton>
              <Eye size={20} />
            </IconButton>
          </Tooltip>

          {momentsEnabled && (
            <Tooltip title={`${t('Duplicate')}`}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  duplicateMomentTemplateTemplate(d, currentUser)
                    .then((duplicateTpl) => {
                      enqueueSnackbarSuccess(
                        t(`Created new moment based on '${d.data.title}'`)
                      );
                      goTo(
                        ROUTES.moments.details.url({
                          momentTemplateId: duplicateTpl.id
                        })
                      );
                    })
                    .catch((e) => {
                      console.warn(e);
                      enqueueSnackbarError(
                        t('Something went wrong. Please try again later')
                      );
                    });
                }}
              >
                <Copy size={20} />
              </IconButton>
            </Tooltip>
          )}
        </FlexContainer>
      </TemplateTemplateCardContainer>
      {previewDialog.dialogOpen && (
        <MomentContentPreviewDialog
          d={d}
          open={true}
          onClose={previewDialog.closeDialog}
          tokens={tokens}
        />
      )}
    </>
  );
};

const MomentTemplateTemplateGrid = styled('div')((p) => ({
  display: 'grid',
  gap: p.theme.spacing(3),
  gridTemplateColumns: 'repeat(3, 1fr)'
}));

const MomentTemplateTemplateList = ({
  tpls,
  loading,
  error
}: {
  tpls: void | Doc<IMomentTemplate>[];
  loading: boolean;
  error: any;
}) => {
  const { t } = useUnsafeTranslation();
  const [selectedFilters, setSelectedFilters] = useState<Categories[]>([]);

  const filteredTpls = useMemo(() => {
    if (!tpls) {
      return null;
    }
    if (!selectedFilters.length) {
      return tpls;
    }
    const filterSet = new Set(selectedFilters);
    return tpls.filter((tpl) =>
      tpl.data.categories.some((c) => filterSet.has(c.type))
    );
  }, [tpls, selectedFilters]);

  if (loading) {
    return <Loader height={300} />;
  }
  if (error || !filteredTpls) {
    return (
      <SomethingWentWrongWithReport
        height={300}
        error={error}
        context={{ page: 'moments_overview', section: 'filtered_templates' }}
      />
    );
  }

  return (
    <>
      <FlexContainer marginBottom={2}>
        {Object.values(Categories).map((f) => {
          const enabled = selectedFilters.includes(f);
          return (
            <ClickableBadgeContainer
              key={f}
              onClick={() => {
                setSelectedFilters((prev) => {
                  return !enabled
                    ? [...prev, f]
                    : prev.filter((prevF) => prevF !== f);
                });
              }}
            >
              <ColoredChip color={enabled ? 'green' : 'gray'}>
                {CategoriesLabels[f](t)}
              </ColoredChip>
            </ClickableBadgeContainer>
          );
        })}
      </FlexContainer>
      {filteredTpls.length ? (
        <MomentTemplateTemplateGrid>
          {filteredTpls.map((m) => (
            <TemplateTemplateCard key={m.id} d={m} />
          ))}
        </MomentTemplateTemplateGrid>
      ) : (
        <Centered height={300}>{t('No moments here')}</Centered>
      )}
    </>
  );
};

const MomentsOverviewBody = () => {
  const { goTo, ROUTES } = useRoutes();
  const { t } = useUnsafeTranslation();
  const currentUser = useCurrentUser();
  const { trackEvent } = useProductAnalytics();
  const [signUpSuccess, setSignUpSuccess] = useBooleanQueryParam(
    'signUpSuccess',
    false
  );
  const momentsEnabled = useFeatureEnabled('MOMENTS_V1');
  const [_momentsTab, setMomentsTab] = useTypedStringQueryParam<MomentFilters>(
    'tab',
    'ALL',
    true
  );
  // Without moments enabled people can only see the Templates tab
  const momentsTab = momentsEnabled
    ? _momentsTab
    : MomentTemplateStatus.TEMPLATE;
  const [
    tpls,
    lodaingTpls,
    errorTpls
  ] = useMappedLoadingValue(
    useMomentTemplatesByOrgId(currentUser.org.id),
    (ds) => sortBy(ds, (d) => -d.data.sendDate.at.toMillis())
  );
  const [
    tplTpls,
    loadingTplTpls,
    errorTplTpls
  ] = useMappedLoadingValue(useMomentTemplateTemplates(), (ds) =>
    sortBy(ds, (d) => d.data.title)
  );

  useEffect(() => {
    trackEvent({
      eventType: PAEventType.OTHER,
      name: 'Moments List Page Loaded',
      Explanation: 'Admin user loaded the Moments list page'
    });
  }, [trackEvent]);

  const filteredTpls = useMemo(() => {
    if (!tpls) {
      return undefined;
    }
    if (momentsTab === 'ALL') {
      return tpls;
    }
    return tpls.filter((m) => m.data.status === momentsTab);
  }, [tpls, momentsTab]);

  const trackingFns = useMemo(() => {
    return {
      onCreate: (
        createdMomentId: string,
        duplicate: 'True' | 'False',
        OriginalMoment?: Doc<IMomentTemplate>
      ) => {
        trackEvent({
          eventType: PAEventType.OTHER,
          name: 'Moment Created',
          Explanation: 'Admin user created a new Moment',
          'Moment ID': createdMomentId,
          Duplicate: duplicate,
          'Original Moment Id': OriginalMoment?.id || 'None',
          'Original Moment Name': OriginalMoment?.data.title || 'None',
          'Original Moment Status': OriginalMoment?.data.status || 'None'
        });
      },
      onReportClicked: (momentName: string, momentId: string) => {
        trackEvent({
          eventType: PAEventType.OTHER,
          name: 'Moment Report Clicked',
          Explanation:
            "Admin user downloaded a report about the Moment's opens, clicks and subtasks",
          'Moment ID': momentId,
          'Moment Name': momentName,
          'Report Medium': 'csv'
        });
      }
    };
  }, [trackEvent]);

  return (
    <>
      <DadoAIBanner />
      <PageBody noTopPadding>
        <PageToolbar sticky>
          <PageToolbarTitle>
            <Typography variant="h1" component="h1">
              {t('Moments')}
            </Typography>
          </PageToolbarTitle>
          <PageToolbarActions>
            {!HIDE_FOR_V1 && (
              <Button
                startIcon={<Search size={16} />}
                variant="outlined"
                color="primary"
                fullWidth
              >
                {t('Search')}
              </Button>
            )}
            {momentsEnabled && (
              <Link>
                <ButtonWithPromise
                  startIcon={<PlusCircle size={16} />}
                  variant="contained"
                  color="primary"
                  pending={t('Creating...')}
                  fullWidth
                  onClick={async () => {
                    const moment = await createNewMomentTemplate(currentUser);
                    trackingFns.onCreate(moment.id, 'False');
                    goTo(
                      ROUTES.moments.details.url({
                        momentTemplateId: moment.id
                      })
                    );
                  }}
                >
                  {t('Create')}
                </ButtonWithPromise>
              </Link>
            )}
          </PageToolbarActions>
        </PageToolbar>
        <StatusFilters
          filter={momentsTab}
          setFilter={setMomentsTab}
          canOnlySeeTemplates={!momentsEnabled}
        />
        <MomentListSection>
          {momentsTab !== MomentTemplateStatus.TEMPLATE && (
            <MomentTemplateList
              tpls={filteredTpls}
              loading={lodaingTpls}
              error={errorTpls}
              onReportDownload={trackingFns.onReportClicked}
              onDuplicate={trackingFns.onCreate}
            />
          )}
          {momentsTab === MomentTemplateStatus.TEMPLATE && (
            <MomentTemplateTemplateList
              tpls={tplTpls}
              loading={loadingTplTpls}
              error={errorTplTpls}
            />
          )}
        </MomentListSection>

        <SignUpSuccessDialog
          open={signUpSuccess}
          onClose={() => setSignUpSuccess(null)}
        />
      </PageBody>
    </>
  );
};

export const PageMomentsOverview = () => {
  // TODO - check for permissions here
  return <MomentsOverviewBody />;
};
